/**
 * Retry utility with exponential backoff
 * Automatically retries failed async operations
 */

/**
 * Retry an async function with exponential backoff
 * @param {Function} fn - Async function to retry
 * @param {Object} options - Retry options
 * @param {number} options.maxRetries - Maximum number of retries (default: 3)
 * @param {number} options.initialDelay - Initial delay in ms (default: 1000)
 * @param {number} options.maxDelay - Maximum delay in ms (default: 10000)
 * @param {Function} options.shouldRetry - Function to determine if error should be retried
 * @returns {Promise} Promise that resolves with the function result
 */
export async function retryWithBackoff(fn, options = {}) {
  const {
    maxRetries = 3,
    initialDelay = 1000,
    maxDelay = 10000,
    shouldRetry = () => true
  } = options;
  
  let lastError;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      
      // Don't retry if we've exhausted retries
      if (attempt >= maxRetries) {
        break;
      }
      
      // Check if we should retry this error
      if (!shouldRetry(error)) {
        throw error;
      }
      
      // Calculate delay with exponential backoff
      const delay = Math.min(
        initialDelay * Math.pow(2, attempt),
        maxDelay
      );
      
      console.warn(`[Retry] Attempt ${attempt + 1}/${maxRetries} failed, retrying in ${delay}ms...`, error.message);
      
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  // All retries exhausted
  console.error(`[Retry] All ${maxRetries} retries exhausted`);
  throw lastError;
}

/**
 * Helper to determine if an error is retryable
 * @param {Error} error - Error to check
 * @returns {boolean} True if error should be retried
 */
export function isRetryableError(error) {
  // Network errors
  if (error.message.includes('fetch failed') || 
      error.message.includes('network') ||
      error.message.includes('timeout')) {
    return true;
  }
  
  // HTTP status codes that should be retried
  if (error.response) {
    const status = error.response.status;
    // Retry on 429 (rate limit), 500+ (server errors), 503 (service unavailable)
    if (status === 429 || status >= 500) {
      return true;
    }
  }
  
  // Don't retry client errors (400-499 except 429)
  return false;
}

export default retryWithBackoff;
